import React, { useEffect, useState } from 'react';
import { splitwiseAPI } from '../../services/api';
import { SplitwiseGroup, MemberMapping, SplitResult } from '../../types';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Avatar } from '../ui/Avatar';

interface SplitwiseSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  billId: string;
  billTotal: number;
  splitResult: SplitResult;
}

type Step = 'group' | 'mapping' | 'review';

export const SplitwiseSyncModal: React.FC<SplitwiseSyncModalProps> = ({
  isOpen,
  onClose,
  billId,
  billTotal,
  splitResult,
}) => {
  const [step, setStep] = useState<Step>('group');
  const [groups, setGroups] = useState<SplitwiseGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<SplitwiseGroup | null>(null);
  const [mappings, setMappings] = useState<MemberMapping[]>([]);
  const [payerSwUserId, setPayerSwUserId] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; warnings?: string[]; error?: string } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep('group');
      setSelectedGroup(null);
      setMappings([]);
      setPayerSwUserId(null);
      setSyncResult(null);
      loadGroups();
    }
  }, [isOpen]);

  const loadGroups = async () => {
    setLoadingGroups(true);
    try {
      const data = await splitwiseAPI.getGroups();
      setGroups(data.groups);
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleSelectGroup = (group: SplitwiseGroup) => {
    setSelectedGroup(group);

    // Auto-match local members to Splitwise members by email
    const newMappings: MemberMapping[] = splitResult.splits.map((split) => {
      // Try to find a matching Splitwise member by name similarity
      const swMatch = group.members.find((m) => {
        const swFullName = `${m.first_name} ${m.last_name}`.toLowerCase().trim();
        const localName = split.userName.toLowerCase().trim();
        return swFullName === localName || m.first_name.toLowerCase() === localName;
      });

      return {
        localUserId: split.userId,
        localUserName: split.userName,
        splitwiseUserId: swMatch?.id || null,
        splitwiseUserName: swMatch ? `${swMatch.first_name} ${swMatch.last_name}` : null,
        autoMatched: !!swMatch,
      };
    });

    setMappings(newMappings);

    // Default payer to first mapped member
    const firstMapped = newMappings.find((m) => m.splitwiseUserId);
    if (firstMapped) {
      setPayerSwUserId(firstMapped.splitwiseUserId);
    }

    setStep('mapping');
  };

  const handleMappingChange = (localUserId: string, swUserId: number | null) => {
    setMappings((prev) =>
      prev.map((m) => {
        if (m.localUserId !== localUserId) return m;
        if (!swUserId) {
          return { ...m, splitwiseUserId: null, splitwiseUserName: null, autoMatched: false };
        }
        const swMember = selectedGroup?.members.find((sm) => sm.id === swUserId);
        return {
          ...m,
          splitwiseUserId: swUserId,
          splitwiseUserName: swMember ? `${swMember.first_name} ${swMember.last_name}` : null,
          autoMatched: false,
        };
      })
    );
  };

  const handleSync = async () => {
    if (!selectedGroup || !payerSwUserId) return;

    setSyncing(true);
    try {
      const result = await splitwiseAPI.syncBill({
        billId,
        splitwiseGroupId: selectedGroup.id,
        memberMappings: mappings,
        payerSplitwiseUserId: payerSwUserId,
      });
      setSyncResult({ success: true, warnings: result.warnings });
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to sync';
      setSyncResult({ success: false, error: msg });
    } finally {
      setSyncing(false);
    }
  };

  const unmappedCount = mappings.filter((m) => !m.splitwiseUserId).length;
  const unmappedTotal = splitResult.splits
    .filter((s) => !mappings.find((m) => m.localUserId === s.userId && m.splitwiseUserId))
    .reduce((sum, s) => sum + s.totalAmount, 0);

  const renderGroupStep = () => (
    <div>
      <p className="text-sm text-slate-500 mb-4">Select a Splitwise group to sync this bill to.</p>
      {loadingGroups ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 border-4 border-slate-200 border-t-green-600 rounded-full animate-spin mx-auto mb-2" />
          <p className="text-sm text-slate-500">Loading groups...</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-slate-500">No Splitwise groups found.</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {groups.map((group) => (
            <button
              key={group.id}
              onClick={() => handleSelectGroup(group)}
              className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-green-300 hover:bg-green-50 transition-colors"
            >
              <p className="font-medium text-slate-900">{group.name}</p>
              <p className="text-xs text-slate-500">{group.members.length} members</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderMappingStep = () => (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        Map each person to their Splitwise account in <span className="font-medium">{selectedGroup?.name}</span>.
      </p>

      <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
        {mappings.map((mapping) => (
          <div key={mapping.localUserId} className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Avatar name={mapping.localUserName} size="sm" />
                <span className="text-sm font-medium text-slate-900 truncate">{mapping.localUserName}</span>
              </div>
            </div>
            <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
            <select
              value={mapping.splitwiseUserId || ''}
              onChange={(e) => handleMappingChange(mapping.localUserId, e.target.value ? Number(e.target.value) : null)}
              className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-green-500/20"
            >
              <option value="">-- Not mapped --</option>
              {selectedGroup?.members.map((sm) => (
                <option key={sm.id} value={sm.id}>
                  {sm.first_name} {sm.last_name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* Payer selection */}
      <div className="border-t border-slate-100 pt-4">
        <label className="text-sm font-medium text-slate-700 block mb-2">Who paid the bill?</label>
        <select
          value={payerSwUserId || ''}
          onChange={(e) => setPayerSwUserId(e.target.value ? Number(e.target.value) : null)}
          className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-green-500/20"
        >
          <option value="">-- Select payer --</option>
          {selectedGroup?.members.map((sm) => (
            <option key={sm.id} value={sm.id}>
              {sm.first_name} {sm.last_name}
            </option>
          ))}
        </select>
      </div>

      {unmappedCount > 0 && (
        <div className="mt-3 p-3 bg-amber-50 rounded-xl">
          <p className="text-xs text-amber-700">
            {unmappedCount} member{unmappedCount > 1 ? 's' : ''} not mapped.
            Their share ({'\u20B9'}{unmappedTotal.toFixed(2)}) will be absorbed by the payer.
          </p>
        </div>
      )}

      <div className="flex gap-3 mt-4">
        <Button variant="secondary" fullWidth onClick={() => setStep('group')}>
          Back
        </Button>
        <Button fullWidth onClick={() => setStep('review')} disabled={!payerSwUserId}>
          Review
        </Button>
      </div>
    </div>
  );

  const renderReviewStep = () => {
    if (syncResult) {
      return (
        <div className="text-center py-4">
          {syncResult.success ? (
            <>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-medium text-slate-900 mb-1">Synced to Splitwise!</p>
              <p className="text-sm text-slate-500 mb-4">The expense has been created in {selectedGroup?.name}.</p>
              {syncResult.warnings && syncResult.warnings.length > 0 && (
                <div className="text-left p-3 bg-amber-50 rounded-xl mb-4">
                  {syncResult.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-700">{w}</p>
                  ))}
                </div>
              )}
              <Button fullWidth onClick={onClose}>
                Done
              </Button>
            </>
          ) : (
            <>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="font-medium text-slate-900 mb-1">Sync Failed</p>
              <p className="text-sm text-red-500 mb-4">{syncResult.error}</p>
              <div className="flex gap-3">
                <Button variant="secondary" fullWidth onClick={onClose}>
                  Cancel
                </Button>
                <Button fullWidth onClick={() => { setSyncResult(null); handleSync(); }}>
                  Retry
                </Button>
              </div>
            </>
          )}
        </div>
      );
    }

    const mappedMembers = mappings.filter((m) => m.splitwiseUserId);
    const payerName = selectedGroup?.members.find((m) => m.id === payerSwUserId);

    return (
      <div>
        <div className="space-y-3 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Group</span>
            <span className="font-medium text-slate-900">{selectedGroup?.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Total</span>
            <span className="font-medium text-slate-900">{'\u20B9'}{billTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Paid by</span>
            <span className="font-medium text-slate-900">
              {payerName ? `${payerName.first_name} ${payerName.last_name}` : 'Unknown'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Members mapped</span>
            <span className="font-medium text-slate-900">{mappedMembers.length} / {mappings.length}</span>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3 mb-4">
          <p className="text-xs font-medium text-slate-500 uppercase mb-2">Split breakdown</p>
          <div className="space-y-2">
            {mappings.map((m) => {
              const split = splitResult.splits.find((s) => s.userId === m.localUserId);
              return (
                <div key={m.localUserId} className="flex justify-between text-sm">
                  <span className={m.splitwiseUserId ? 'text-slate-900' : 'text-slate-400 line-through'}>
                    {m.localUserName} {m.splitwiseUserName && m.localUserName !== m.splitwiseUserName ? `\u2192 ${m.splitwiseUserName}` : ''}
                  </span>
                  <span className={m.splitwiseUserId ? 'font-medium text-slate-900' : 'text-slate-400'}>
                    {'\u20B9'}{split ? split.totalAmount.toFixed(2) : '0.00'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {unmappedCount > 0 && (
          <div className="p-3 bg-amber-50 rounded-xl mb-4">
            <p className="text-xs text-amber-700">
              {unmappedCount} unmapped member{unmappedCount > 1 ? 's' : ''}: their {'\u20B9'}{unmappedTotal.toFixed(2)} share will be added to the payer's owed amount.
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="secondary" fullWidth onClick={() => setStep('mapping')}>
            Back
          </Button>
          <Button fullWidth onClick={handleSync} loading={syncing}>
            Sync to Splitwise
          </Button>
        </div>
      </div>
    );
  };

  const titles: Record<Step, string> = {
    group: 'Select Splitwise Group',
    mapping: 'Map Members',
    review: syncResult?.success ? 'Synced!' : 'Review & Confirm',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={titles[step]} size="md">
      {step === 'group' && renderGroupStep()}
      {step === 'mapping' && renderMappingStep()}
      {step === 'review' && renderReviewStep()}
    </Modal>
  );
};

export default SplitwiseSyncModal;
